import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { toast } from "sonner";
import { 
  Truck, 
  Package, 
  Sparkles, 
  Archive, 
  Zap, 
  Sofa, 
  Calendar, 
  CheckCircle2, 
  MapPin, 
  ArrowRight,
  ShieldCheck,
  Building
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const AVAILABLE_SERVICES = [
  { id: "Removals", title: "White-Glove Removals", desc: "Insured transit & furniture assembly across London", icon: Truck },
  { id: "Packing & Unpacking", title: "Packing & Unpacking", desc: "Specialist crating for fine art, mirrors, and glass", icon: Package },
  { id: "End-of-Tenancy Deep Clean", title: "End-of-Tenancy Deep Clean", desc: "Guaranteed deposit return cleaning certification", icon: Sparkles },
  { id: "Short Stay Interim", title: "Short Stay Interim Staging", desc: "Turnkey interim stays during lease transitions", icon: Building },
  { id: "Secure Storage", title: "Secure Climate-Controlled Storage", desc: "Short & long-term storage facilities in London", icon: Archive },
  { id: "Utilities Concierge", title: "Utilities & Broadband Setup", desc: "Fibre broadband, council tax & energy transfer", icon: Zap },
  { id: "Furniture Setup", title: "Furniture & Interior Setup", desc: "Rental furniture staging and bespoke fit-outs", icon: Sofa }
];

export const MoveDispatch = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submittedReq, setSubmittedReq] = useState(null);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    services: ["Removals", "Packing & Unpacking", "End-of-Tenancy Deep Clean"],
    move_date: "2026-07-01",
    pickup_postcode: "NW3 2QG",
    delivery_postcode: "W8 4PT",
    property_size: "2-Bed Flat",
    special_requirements: "Delicate artwork and antique mirror requiring crating."
  });

  const toggleService = (srvId) => {
    setFormData((prev) => {
      const exists = prev.services.includes(srvId);
      if (exists) {
        if (prev.services.length === 1) return prev;
        return { ...prev, services: prev.services.filter((s) => s !== srvId) };
      } else {
        return { ...prev, services: [...prev.services, srvId] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all contact details.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/move-requests", formData);
      setSubmittedReq(res.data);
      toast.success("FIND LET Move inquiry dispatched to specialist partners!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit move inquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center space-y-2 mb-10">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#D0E4FE] text-amber-900 text-xs font-semibold">
            <Truck className="w-3.5 h-3.5" />
            <span>FIND LET Move Dispatch</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#091F5C] font-heading">
            Vetted London Relocation & Property Services
          </h1>
          <p className="text-[#4A5B78] text-sm max-w-xl mx-auto">
            Book certified white-glove removals, deposit-guaranteed deep cleaning, secure storage, and utility setup through our verified specialist partners.
          </p>
        </div>

        {submittedReq ? (
          <div className="bg-white rounded-2xl p-8 border border-[#E5EBF5] shadow-xl text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-[#D0E4FE] text-[#334DAF] flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-[#091F5C] font-heading">
                Move Inquiry Assigned to Concierge Dispatch
              </h2>
              <p className="text-sm text-[#4A5B78]">
                Reference: <span className="font-mono font-bold text-[#091F5C]">{submittedReq.id?.slice(0, 8)}</span>
              </p>
              <p className="text-xs text-[#4A5B78] max-w-md mx-auto">
                We have notified our partner logistics dispatchers for route <strong className="text-[#091F5C]">{submittedReq.pickup_postcode} &rarr; {submittedReq.delivery_postcode}</strong>. You will receive firm quote estimates shortly.
              </p>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/tenant/dashboard"
                data-testid="move-view-dashboard-btn"
                className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-sm shadow"
              >
                Open Tenant Dashboard
              </Link>
              <button
                onClick={() => setSubmittedReq(null)}
                className="px-6 py-3 rounded-xl border border-[#D0E4FE] text-[#4A5B78] font-semibold text-sm"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-10 border border-[#E5EBF5] shadow-xl space-y-8">
            
            {/* Step 1: Select Services */}
            <div>
              <h3 className="text-lg font-bold text-[#091F5C] font-heading mb-1">
                1. Select Relocation & Preparation Services
              </h3>
              <p className="text-xs text-[#4A5B78] mb-4">
                Choose the services required for your upcoming London move.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {AVAILABLE_SERVICES.map((srv) => {
                  const isSelected = formData.services.includes(srv.id);
                  const Icon = srv.icon;
                  return (
                    <button
                      key={srv.id}
                      type="button"
                      onClick={() => toggleService(srv.id)}
                      data-testid={`move-service-card-${srv.id.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
                      className={`p-4 rounded-xl border text-left transition-all relative ${
                        isSelected 
                          ? "bg-[#E8F2FE]/70 border-[#D0E4FE] text-amber-950 font-medium shadow-sm"
                          : "border-[#E5EBF5] hover:border-[#D0E4FE] text-[#4A5B78]"
                      }`}
                    >
                      <div className="flex items-center space-x-3 mb-2">
                        <div className={`p-2 rounded-lg ${isSelected ? "bg-[#334DAF] text-white" : "bg-[#E8F2FE] text-[#4A5B78]"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold font-heading">{srv.title}</span>
                      </div>
                      <p className="text-[11px] text-[#4A5B78] leading-normal">{srv.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Route & Property Specs */}
            <div className="pt-4 border-t space-y-4">
              <h3 className="text-lg font-bold text-[#091F5C] font-heading">
                2. Move Timing & Location Details
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Target Move Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.move_date}
                    onChange={(e) => setFormData({ ...formData, move_date: e.target.value })}
                    data-testid="input-move-date"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Pickup Postcode *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. NW3 2QG"
                    value={formData.pickup_postcode}
                    onChange={(e) => setFormData({ ...formData, pickup_postcode: e.target.value })}
                    data-testid="input-pickup-postcode"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono uppercase"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Delivery Postcode *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. W8 4PT"
                    value={formData.delivery_postcode}
                    onChange={(e) => setFormData({ ...formData, delivery_postcode: e.target.value })}
                    data-testid="input-delivery-postcode"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Property Size
                  </label>
                  <select
                    value={formData.property_size}
                    onChange={(e) => setFormData({ ...formData, property_size: e.target.value })}
                    data-testid="select-move-property-size"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    <option value="Studio / 1-Bed">Studio / 1-Bed Flat</option>
                    <option value="2-Bed Flat">2-Bed Flat</option>
                    <option value="3-Bed House">3-Bed House</option>
                    <option value="4+ Bed House">4+ Bed House / Penthouse</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Special Requirements (Art, Piano, Glass)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Grand piano, lift access restrictions"
                    value={formData.special_requirements}
                    onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value })}
                    data-testid="input-move-special-reqs"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Step 3: Contact Details */}
            <div className="pt-4 border-t space-y-4">
              <h3 className="text-lg font-bold text-[#091F5C] font-heading">
                3. Contact Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Eleanor Vane"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    data-testid="input-move-name"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. eleanor@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="input-move-email"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+44 7700 900342"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="input-move-phone"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-move-dispatch-btn"
                className="px-8 py-3.5 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-bold text-sm shadow-lg shadow-amber-700/30 transition-all flex items-center space-x-2 disabled:opacity-50"
              >
                <Truck className="w-4 h-4" />
                <span>{submitting ? "Dispatching Inquiry..." : "Dispatch FIND LET Move Inquiry"}</span>
              </button>
            </div>

          </form>
        )}

      </div>
    </div>
  );
};
